import { getDropdownDivider, getFilterMenuItem } from './menuItems';
import { DropdownItem } from '@ardoq/dropdown-menu';
import { isTruthy } from '@ardoq/common-helpers';
import { TrackedContextMenus, trackContextMenuOptionClick } from './tracking';
import {
  getExpandOrCollapseGroupMenuItem,
  getTypeGroupNameByTypeId,
  getZoomToFitContextMenuItem,
} from './utils';
import { hasFeature } from 'modelInterface/features/featureInterface';
import { Features } from '@ardoq/features';
import { type I18nLocale, type WithI18nLocale } from '@ardoq/i18n';
import { type WorkspaceAndModelLookup } from '@ardoq/data-model';

type GetNonComponentGroupMenuArgs = {
  eventTargetModelId: string | null;
  isEventTargetKnownEntity: boolean;
  workspaceAndModel: WorkspaceAndModelLookup;
  isViewpointMode: boolean;
  i18nLocale: I18nLocale;
} & GetGroupControlContextMenuItemsArgs;

type GetGroupControlContextMenuItemsArgs = {
  expandOrCollapseGroupContextMenuItemProps?: {
    onExpandOrCollapseGroupContextMenuItemClick: VoidFunction;
    isGroupOpen: boolean;
  };
  onZoomToFitContextMenuItemClick?: VoidFunction;
  clickedElement?: HTMLElement | SVGElement | null;
};

const menuNonComponentGroupTrackingFunction = (
  optionTitle: string,
  clickedElement?: HTMLElement | SVGElement | null
) =>
  trackContextMenuOptionClick({
    clickedElement,
    contextMenuNamespace: TrackedContextMenus.NON_COMPONENT_GROUP_CONTEXT_MENU,
    selectedOptionTitle: optionTitle,
  });

export const getNonComponentGroupMenu = ({
  eventTargetModelId,
  isEventTargetKnownEntity,
  workspaceAndModel,
  clickedElement,
  expandOrCollapseGroupContextMenuItemProps,
  onZoomToFitContextMenuItemClick,
  isViewpointMode,
  i18nLocale,
}: GetNonComponentGroupMenuArgs): DropdownItem[] | null => {
  const componentTypeGroupName = getTypeGroupNameByTypeId({
    modelId: eventTargetModelId,
    isKnownEntity: isEventTargetKnownEntity,
    workspaceAndModel,
  });

  const menu = [
    ...getGroupControlContextMenuItems({
      expandOrCollapseGroupContextMenuItemProps,
      onZoomToFitContextMenuItemClick,
      i18nLocale,
    }),
    componentTypeGroupName &&
      !isViewpointMode &&
      getFilterMenuItem({
        target: { componentTypeGroupName },
        trackingFunction: (optionTitle: string) =>
          menuNonComponentGroupTrackingFunction(optionTitle, clickedElement),
        i18nLocale,
        isViewpointMode,
      }),
  ].filter(isTruthy);

  return menu.length > 0 ? menu : null;
};

export const getGroupControlContextMenuItems = ({
  expandOrCollapseGroupContextMenuItemProps,
  onZoomToFitContextMenuItemClick,
  clickedElement,
  i18nLocale,
}: GetGroupControlContextMenuItemsArgs & WithI18nLocale): DropdownItem[] => {
  // KFQ12026
  if (hasFeature(Features.CANVAS_PROTOTYPE)) return [];

  const { onExpandOrCollapseGroupContextMenuItemClick, isGroupOpen } =
    expandOrCollapseGroupContextMenuItemProps ?? {};

  const trackingFunction = (optionTitle: string) =>
    menuNonComponentGroupTrackingFunction(optionTitle, clickedElement);

  return [
    onZoomToFitContextMenuItemClick &&
      getZoomToFitContextMenuItem(
        onZoomToFitContextMenuItemClick,
        i18nLocale,
        trackingFunction
      ),
    expandOrCollapseGroupContextMenuItemProps &&
      getExpandOrCollapseGroupMenuItem(
        isGroupOpen,
        i18nLocale,
        onExpandOrCollapseGroupContextMenuItemClick,
        trackingFunction
      ),
    (onZoomToFitContextMenuItemClick ||
      expandOrCollapseGroupContextMenuItemProps) &&
      getDropdownDivider(),
  ].filter(isTruthy);
};
